/**
 * featured-collections-nosto — fetches Nosto recommendations for the tabs of
 * `sections/ax-featured-collections.liquid` whose block has a `nosto_placement`
 * configured, and grafts them onto La Belle Boucle's *shared-swiper* tab
 * architecture.
 *
 * Architecture of the host section (labelleboucle)
 * ------------------------------------------------
 *   Unlike the Coucou theme (one `<featured-collection>` per tab), labelleboucle
 *   renders ONE `<featured-collection>` per section containing:
 *     - a single shared `.swiper` / `.swiper-wrapper`
 *     - tab headings `.featured-collections__heading[data-index]`
 *       (1-based, matching the Liquid `forloop.index` of each block) ; the
 *       first heading is `.visible` at SSR.
 *     - slides `.swiper-slide[data-index="N"]` ; only the first tab is rendered
 *       at SSR (lazy-load), other tabs are fetched on click by
 *       `bundle.featured-collections.js` via `loadCollectionSlides(index)`
 *       (`?section_id=…&collection_index=N`).
 *
 *   The bundle's tab logic (`AxFeaturedCollections`):
 *     - heading click → `navActive` (toggle `.visible` on headings)
 *                     → `slidesActive(index)` : if NO `.swiper-slide[data-index=index]`
 *                       exists yet, `await loadCollectionSlides(index)`, then
 *                       toggle `.visible .active` on the slides whose
 *                       `data-index === index`.
 *                     → `buttonActive` + `swiperInstance.slideTo(0)`.
 *
 * How this script integrates — it ADDS to that flow, never overrides it
 * --------------------------------------------------------------------
 *   For each tab heading marked `data-nosto-placement="…"` (set by Liquid when
 *   the block has a non-empty `nosto_placement`), we:
 *     1. fetch the Nosto placement (`createRecommendationRequest`
 *        → parse `[data-nosto-recommendation]` JSON island → `parsed.products`),
 *     2. fetch each product card via `/products/{handle}?section_id=card-product-nosto-reco`
 *        (carte calquée sur featured-collection / related-products : square +
 *        quick-add compact, sans bouton ATC pleine largeur),
 *     3. inject the resulting cards as `.swiper-slide[data-index="N"]` into the
 *        SHARED `.swiper-wrapper` — WITHOUT ever doing `innerHTML = ''` on the
 *        wrapper (that would destroy the other tabs' slides).
 *
 *   Because we inject slides carrying the right `data-index` BEFORE the user can
 *   click, the bundle's `slidesActive(index)` finds them already present and so
 *   never calls `loadCollectionSlides` for a Nosto tab. The native
 *   `.visible/.active` toggling then "just works".
 *
 * Trigger
 * -------
 *   - The Nosto tab that is `.visible` at SSR (always the first tab) → fetch on
 *     boot and make its slides visible.
 *   - Other Nosto tabs → background pre-load on idle (so slides exist by the time
 *     the user clicks), with a heading-click safety net.
 *
 * No dependency on `bundle.featured-collections.js` internals beyond the public
 * `swiperInstance` property the custom element exposes after init.
 */
(function () {
	if (typeof window === 'undefined') return;
	if (window.__featuredCollectionsNostoInstalled) return;
	window.__featuredCollectionsNostoInstalled = true;

	const SECTION_NAME = 'card-product-nosto-reco';
	const RECO_SELECTOR = '[data-nosto-recommendation]';
	const NOSTO_TIMEOUT_MS = 10000;
	// Au-delà de ce délai (Nosto absent, lent ou en erreur), on abandonne le
	// skeleton et on affiche la configuration classique (produits de collection).
	const FALLBACK_TIMEOUT_MS = 5000;
	const NOSTO_POLL_MS = 100;
	const SWIPER_POLL_MS = 100;
	const SWIPER_TIMEOUT_MS = 5000;

	// Cache: placementId → Promise<{ products, resultId }>
	const placementCache = new Map();

	function waitForNosto() {
		return new Promise(function (resolve, reject) {
			let spent = 0;
			const tick = function () {
				if (typeof window.nostojs === 'function') return resolve();
				if (spent >= NOSTO_TIMEOUT_MS) return reject(new Error('Nosto load timeout'));
				spent += NOSTO_POLL_MS;
				setTimeout(tick, NOSTO_POLL_MS);
			};
			tick();
		});
	}

	// Extrait une liste de produits depuis le HTML du template Nosto par défaut :
	// un produit par lien `/products/{handle}` (dédupliqué).
	function extractProductsFromHtml(root) {
		const seen = new Set();
		const products = [];
		root.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
			const url = a.getAttribute('href');
			if (!url) return;
			const handle = url.split('?')[0].replace(/\/+$/, '').split('/').pop();
			if (!handle || seen.has(handle)) return;
			seen.add(handle);
			const idHost = a.closest('[data-product-id]') || a.querySelector('[data-product-id]');
			const label = a.getAttribute('aria-label') || '';
			products.push({
				product_url: url,
				product_id: a.getAttribute('data-product-id') || (idHost && idHost.getAttribute('data-product-id')) || null,
				product_name: label.replace(/^\s*View product:\s*/i, '') || null
			});
		});
		return products;
	}

	// Tolère DEUX formats de template Nosto : (1) île JSON
	// `[data-nosto-recommendation]{products:[…]}` (template custom) ;
	// (2) HTML de cartes produit du template Nosto PAR DÉFAUT (liens /products/).
	function parseRecommendedProducts(html) {
		if (!html) return [];
		try {
			const tmp = document.createElement('div');
			tmp.innerHTML = html;
			const node = tmp.querySelector(RECO_SELECTOR);
			if (node) {
				const parsed = JSON.parse(node.textContent);
				if (parsed && Array.isArray(parsed.products) && parsed.products.length) {
					return parsed.products;
				}
			}
			return extractProductsFromHtml(tmp);
		} catch (err) {
			console.error('[FeaturedCollectionsNosto] parse failed:', err);
			return [];
		}
	}

	function extractHandle(url) {
		const noQuery = String(url).split('?')[0].replace(/\/+$/, '');
		const parts = noQuery.split('/');
		return parts[parts.length - 1];
	}

	async function fetchCard(product) {
		try {
			const handle = extractHandle(product.product_url);
			// Jamais `/products/{id}` : un handle purement numérique est un ID
			// produit (feed Nosto sans handle) → 404. On saute le produit.
			if (!handle || /^\d+$/.test(handle)) return null;
			const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
			const url = root + 'products/' + encodeURIComponent(handle) + '?section_id=' + SECTION_NAME;
			const resp = await fetch(url, { credentials: 'same-origin' });
			if (!resp.ok) return null;
			const html = await resp.text();
			if (/^<!doctype/i.test(html.trim())) return null;
			const tmp = document.createElement('div');
			tmp.innerHTML = html;
			const card = tmp.querySelector('.card-wrapper') || tmp.querySelector('.product-card-wrapper');
			if (!card) return null;
			// Complète product_id depuis la carte (template Nosto par défaut :
			// pas d'id dans le HTML de reco) pour garder le tracking au clic.
			if (!product.product_id) {
				const idEl = card.matches('[data-product-id]') ? card : card.querySelector('[data-product-id]');
				if (idEl) product.product_id = idEl.getAttribute('data-product-id');
			}
			return { product: product, cardEl: card };
		} catch (_) {
			return null;
		}
	}

	// Request a placement once, cache the parsed product list + Nosto result id.
	// TOUJOURS résolu (jamais rejeté) : sur crash Nosto (waitForNosto reject ou
	// .load() en erreur) OU délai trop long (FALLBACK_TIMEOUT_MS), on résout sur
	// une liste vide → l'appelant bascule alors sur la configuration classique.
	function requestPlacement(placement) {
		if (placementCache.has(placement)) return placementCache.get(placement);
		const core = (async function () {
			await waitForNosto();
			const reco = await new Promise(function (resolve) {
				window.nostojs(function (api) {
					api.createRecommendationRequest({ includeTagging: true })
						.setElements([placement])
						.load({ skipEvents: true })
						.then(function (response) {
							resolve(response && response.recommendations && response.recommendations[placement]);
						})
						.catch(function (err) {
							console.error('[FeaturedCollectionsNosto] recommendation request failed:', err);
							resolve(null);
						});
				});
			});
			if (!reco) return { products: [], resultId: null };
			const products = parseRecommendedProducts(reco.html);
			return { products: products, resultId: reco.result_id || null };
		})();
		const guarded = Promise.race([
			core.catch(function (err) {
				console.error('[FeaturedCollectionsNosto] placement error → fallback classique:', err);
				return { products: [], resultId: null };
			}),
			new Promise(function (resolve) {
				setTimeout(function () { resolve({ products: [], resultId: null, timedOut: true }); }, FALLBACK_TIMEOUT_MS);
			})
		]);
		placementCache.set(placement, guarded);
		return guarded;
	}

	// Resolve the custom element's `swiperInstance` (set by the bundle after
	// init). Poll briefly in case Swiper init runs after our async fetch.
	function findSwiperInstance(el) {
		return new Promise(function (resolve) {
			let spent = 0;
			const tick = function () {
				if (el.swiperInstance) return resolve(el.swiperInstance);
				if (spent >= SWIPER_TIMEOUT_MS) return resolve(null);
				spent += SWIPER_POLL_MS;
				setTimeout(tick, SWIPER_POLL_MS);
			};
			tick();
		});
	}

	// Nosto product-click tracking — same call shape as
	// `nosto-recommendations-sidecart` (`setProducts([...], placement)`).
	function bindTracking(slide, productId, placement) {
		if (!productId || slide.__nostoTrackingBound) return;
		slide.__nostoTrackingBound = true;
		slide.querySelectorAll('a').forEach(function (link) {
			link.addEventListener('click', function () {
				window.nostojs(function (api) {
					api.createRecommendationRequest()
						.setProducts([{ product_id: productId }], placement)
						.load()
						.catch(function () {});
				});
			});
		});
	}

	// Are this tab's Nosto slides already in the shared wrapper?
	function tabHasSlides(wrapper, index) {
		return wrapper.querySelectorAll('.swiper-slide[data-index="' + index + '"]').length > 0;
	}

	// Is the given tab (by data-index) the currently visible/active one?
	function isTabActive(featuredEl, index) {
		const heading = featuredEl.querySelector('.featured-collections__heading[data-index="' + index + '"]');
		return !!(heading && heading.classList.contains('visible'));
	}

	/**
	 * Load one Nosto tab.
	 * @param {HTMLElement} featuredEl  the <featured-collection> custom element
	 * @param {HTMLElement} heading     the .featured-collections__heading[data-index] for this tab
	 */
	function loadTab(featuredEl, heading) {
		const index = heading.dataset.index;
		const placement = heading.dataset.nostoPlacement;
		if (!index || !placement) return Promise.resolve();

		// Promesse mémoïsée par onglet : un seul chargement réel par index, et
		// les appelants (boot, preload idle, clic, promote de repli) partagent /
		// attendent la MÊME promesse. promoteFallbackTab s'appuie là-dessus pour
		// attendre que les slides Nosto soient injectés AVANT d'activer l'onglet
		// (sinon slidesActive() natif fetch la collection → doublons).
		const loads = featuredEl.__nostoTabLoads || (featuredEl.__nostoTabLoads = {});
		if (loads[index]) return loads[index];
		loads[index] = loadTabImpl(featuredEl, heading, index, placement);
		return loads[index];
	}

	// --- Résolution des onglets Nosto (skeleton SSR → Nosto ou fallback) -----

	// Retire les slides skeleton (rendues au SSR) de cet onglet.
	function removeSkeletonSlides(wrapper, index) {
		wrapper.querySelectorAll('.swiper-slide.nosto-fc__skeleton-slide[data-index="' + index + '"]')
			.forEach(function (s) { s.remove(); });
	}

	// Recalcule Swiper après mutation du wrapper partagé.
	async function refreshSwiper(featuredEl, active) {
		const swiper = await findSwiperInstance(featuredEl);
		if (swiper && typeof swiper.update === 'function') {
			swiper.update();
			if (active && typeof swiper.slideTo === 'function') swiper.slideTo(0, 0);
		}
	}

	// Nosto vide / crash / timeout → CONFIGURATION CLASSIQUE. Aucun produit
	// classique n'est dans le DOM au load (skeleton seul) : on déclenche le
	// chargement natif de la collection de l'onglet (fetch
	// `?section_id=…&collection_index=N`), exactement comme un onglet lazy au
	// clic. Filet : si la collection est vide / l'API native indisponible,
	// masquer l'onglet.
	async function fallbackToClassic(featuredEl, heading, wrapper, index, tabState, active) {
		// Fetch CONTRÔLÉ de la config classique de CET onglet uniquement — on NE
		// délègue PAS au natif `slidesActive` (qui refetch toute la section et
		// duplique les autres onglets). Le skeleton reste affiché PENDANT le fetch,
		// puis SWAP ATOMIQUE (aucun trou vide, aucun doublon).
		const sectionId = featuredEl.dataset.sectionId;
		let classic = [];
		if (sectionId) {
			try {
				const url = window.location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&collection_index=' + index;
				const html = await fetch(url, { credentials: 'same-origin' }).then(function (r) { return r.ok ? r.text() : ''; });
				const tmp = document.createElement('div');
				tmp.innerHTML = html;
				classic = Array.from(tmp.querySelectorAll('.nosto-fc__fallback-slide[data-index="' + index + '"]'));
			} catch (e) {
				console.error('[FeaturedCollectionsNosto] fetch fallback classique échoué:', e);
			}
		}
		// Swap atomique (même tâche) : retrait skeleton + injection classique.
		// On ré-évalue l'état actif MAINTENANT (l'utilisateur a pu cliquer
		// l'onglet pendant le fetch) pour que les cartes reçoivent `.visible`.
		const activeNow = isTabActive(featuredEl, index);
		removeSkeletonSlides(wrapper, index);
		if (!classic.length) {
			hideEmptyTab(featuredEl, heading, wrapper, index, tabState);
			return;
		}
		classic.forEach(function (slide) {
			slide.classList.remove('nosto-fc__fallback-slide');
			if (activeNow) slide.classList.add('visible'); else slide.classList.remove('visible');
			wrapper.appendChild(slide);
		});
		tabState[index] = 'fallback';
		await refreshSwiper(featuredEl, activeNow);
		document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
		if (typeof window.refreshJudgemeWidgets === 'function') window.refreshJudgemeWidgets();
	}

	// Le skeleton est rendu au SSR (skeleton seul, pas de produits classiques).
	// Le JS ne fait que RÉSOUDRE : cartes Nosto (succès) ou config classique
	// chargée à la demande (vide / crash / timeout).
	async function loadTabImpl(featuredEl, heading, index, placement) {
		const tabState = featuredEl.__nostoTabs || (featuredEl.__nostoTabs = {});
		tabState[index] = 'loading';

		const wrapper = featuredEl.querySelector('.swiper-wrapper');
		if (!wrapper) { tabState[index] = 'idle'; return; }

		const productsToShow = parseInt(heading.dataset.nostoProductsToShow, 10) || 8;
		const active = isTabActive(featuredEl, index);

		// requestPlacement est TOUJOURS résolu (liste vide sur crash/timeout).
		const result = await requestPlacement(placement);
		const products = (result.products || []).slice(0, productsToShow);

		// Vide / crash / timeout → configuration classique.
		if (products.length === 0) {
			fallbackToClassic(featuredEl, heading, wrapper, index, tabState, active);
			return;
		}

		const fetched = await Promise.all(products.map(fetchCard));
		const valid = fetched.filter(Boolean);
		if (valid.length === 0) {
			fallbackToClassic(featuredEl, heading, wrapper, index, tabState, active);
			return;
		}

		// Succès → cartes Nosto : on retire le skeleton SSR de cet onglet.
		// IMPORTANT : on ré-évalue l'état actif MAINTENANT (l'utilisateur a pu
		// cliquer l'onglet pendant le fetch async). Sinon les cartes injectées
		// n'auraient pas `.visible` et l'onglet actif resterait vide
		// (`.swiper-slide:not(.visible){display:none}`).
		const activeNow = isTabActive(featuredEl, index);
		removeSkeletonSlides(wrapper, index);

		const sliderId = (wrapper.closest('[id^="Swiper-"]') || {}).id || 'Swiper-nosto';
		// Animation de reveal au switch d'onglet, iso aux slides natifs
		// (`scroll-trigger animate--slide-in` + cascade). base.css joue l'anim
		// dès que la slide n'a pas `.scroll-trigger--offscreen` — ce qui est le
		// cas des slides injectées dans l'onglet visible.
		const revealAnim = featuredEl.dataset.revealAnim === 'true';
		valid.forEach(function (entry, i) {
			const slide = document.createElement('div');
			slide.className = 'swiper-slide grid__item';
			if (revealAnim) {
				slide.classList.add('scroll-trigger', 'animate--slide-in');
				slide.setAttribute('data-cascade', '');
				slide.style.setProperty('--animation-order', i + 1);
			}
			// Mirror the active/visible state of the native first tab so the
			// shared swiper shows them when this tab is the visible one.
			if (activeNow) slide.classList.add('visible', 'active');
			slide.id = sliderId + '-nosto-' + index + '-' + (i + 1);
			slide.dataset.index = index;
			slide.dataset.nostoProductId = entry.product.product_id;
			slide.dataset.nostoTrackingUrl = entry.product.product_url;
			slide.appendChild(entry.cardEl);
			bindTracking(slide, entry.product.product_id, result.resultId || placement);
			wrapper.appendChild(slide);
		});

		tabState[index] = 'done';
		await refreshSwiper(featuredEl, activeNow);

		// Reactivate lazy observers (images) for the freshly injected cards.
		document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
		if (typeof window.refreshJudgemeWidgets === 'function') window.refreshJudgemeWidgets();
	}

	// Le centrage natif des onglets repose sur
	// `.featured-collections__heading:first-child{margin-left:auto}` et
	// `:last-child{margin-right:auto}`. Quand un onglet Nosto vide est masqué
	// (display:none), ces pseudo-classes continuent de cibler un onglet sans
	// boite de layout : les onglets ENCORE VISIBLES perdent alors leur centrage
	// et se collent à gauche (cf. prod où tous les onglets ont du contenu). On
	// ré-applique donc les marges auto au premier et au dernier onglet visible.
	function recenterHeadings(featuredEl) {
		const all = Array.from(featuredEl.querySelectorAll('.featured-collections__heading'));
		if (!all.length) return;
		all.forEach(function (h) { h.style.marginLeft = ''; h.style.marginRight = ''; });
		const visible = all.filter(function (h) {
			return h.style.display !== 'none' && getComputedStyle(h).display !== 'none';
		});
		if (!visible.length) return;
		visible[0].style.marginLeft = 'auto';
		visible[visible.length - 1].style.marginRight = 'auto';
	}

	// Masque un onglet Nosto vide. IMPORTANT : on capture l'état actif AVANT de
	// retirer `.visible` (sinon le garde de promoteFallbackTab échouait toujours
	// et aucun onglet de repli n'était activé). On retire aussi les slides SSR de
	// repli de cet index pour ne pas les laisser affichés sous un heading masqué.
	function hideEmptyTab(featuredEl, heading, wrapper, index, tabState) {
		tabState[index] = 'empty';
		const wasActive = heading.classList.contains('visible');
		heading.style.display = 'none';
		heading.classList.remove('visible');
		if (wrapper) {
			wrapper.querySelectorAll('.swiper-slide[data-index="' + index + '"]').forEach(function (s) { s.remove(); });
		}
		featuredEl.querySelectorAll('.featured-collections__btn[data-index="' + index + '"]').forEach(function (btn) {
			btn.style.display = 'none';
			btn.classList.remove('active');
		});
		recenterHeadings(featuredEl);
		if (wasActive) promoteFallbackTab(featuredEl, index);
	}

	// Bascule sur le premier onglet encore affiché (heading non masqué) pour ne
	// jamais laisser la section sans onglet actif. Le clic déclenche la logique
	// native (navActive + slidesActive) qui active heading + slides de l'onglet.
	async function promoteFallbackTab(featuredEl, emptyIndex) {
		const headings = Array.from(featuredEl.querySelectorAll('.featured-collections__heading'));
		const next = headings.find(function (h) {
			return h.style.display !== 'none' && h.dataset.index !== emptyIndex;
		});
		if (!next) return;
		// Si l'onglet de repli est un onglet Nosto, on attend que ses cartes
		// soient injectées AVANT de cliquer : ainsi slidesActive() natif les
		// trouve déjà présentes et ne va PAS fetch la collection (ce qui
		// empilerait cartes Nosto + produits de collection en double).
		const wrapper = featuredEl.querySelector('.swiper-wrapper');
		if (next.dataset.nostoPlacement && wrapper && !tabHasSlides(wrapper, next.dataset.index)) {
			await loadTab(featuredEl, next);
			// L'onglet de repli a pu lui-même se révéler vide (et déjà promouvoir
			// un autre onglet via sa propre chaîne) → ne pas le réactiver.
			if (next.style.display === 'none') return;
		}
		next.click();
	}

	// Pre-load a non-active Nosto tab once the page is idle, so the slides exist
	// (with the right data-index) before the user clicks — preventing the bundle
	// from ever firing its own `loadCollectionSlides` for a Nosto tab.
	function schedulePreload(fn) {
		if (typeof window.requestIdleCallback === 'function') {
			window.requestIdleCallback(fn, { timeout: 2500 });
		} else {
			setTimeout(fn, 1500);
		}
	}

	function initFeaturedCollection(featuredEl) {
		const headings = Array.from(
			featuredEl.querySelectorAll('.featured-collections__heading[data-nosto-placement]')
		);
		if (!headings.length) return;

		headings.forEach(function (heading) {
			const wrapper = featuredEl.querySelector('.swiper-wrapper');
			const index = heading.dataset.index;
			const active = heading.classList.contains('visible');

			// (1) Active Nosto tab → load on boot.
			// (2) Other Nosto tabs → background pre-load (idempotent guards make
			//     this safe even if the click safety-net fires first).
			if (active) {
				loadTab(featuredEl, heading);
			} else if (!wrapper || !tabHasSlides(wrapper, index)) {
				schedulePreload(function () { loadTab(featuredEl, heading); });
			}

			// (3) Heading-click safety net: if the user clicks before the idle
			// pre-load resolved, kick the fetch immediately. `loadTab` is
			// idempotent via the per-tab state map.
			heading.addEventListener('click', function () {
				loadTab(featuredEl, heading);
			});
		});
	}

	function init() {
		const elements = document.querySelectorAll('.featured-collections featured-collection');
		if (!elements.length) return;
		elements.forEach(function (el) {
			// Only sections that actually have at least one Nosto heading do work.
			if (el.querySelector('.featured-collections__heading[data-nosto-placement]')) {
				initFeaturedCollection(el);
			}
		});
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init, { once: true });
	} else {
		init();
	}
})();
